import classNames from "classnames";
import { EMPTY_ARRAY } from "client/Misc/utils";
import {
	forwardRef,
	MouseEvent,
	ReactElement,
	Ref,
	useCallback,
	useContext,
	useMemo,
	KeyboardEvent
} from "react";
import { ButtonClickEventArgs, PrimaryButtonColor } from "../Button/Button";
import { ButtonBase } from "../ButtonBase/ButtonBase";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
 } from "../ControlAttachment";
import {
	FontIconName,
	FontIconValue,
	IconValue,
	SvgIconValue,
	unsetIconColor
 } from "../Icon/Icon";
import { kindToHoverStyleMapping } from "./common";
import styles from "./IconButton.module.less";
import { IconButtonRenderer } from "./IconButtonRenderer";

export type ExplicitInheritedTransparentColoredIconValue = FontIconValue & { color: "inherit"; };

export type IconButtonProps = {
	onClick?(arg: ButtonClickEventArgs): void;
	onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
	trackingId: string | undefined;
	hasNoBorderRadius?: boolean;
	size?: IconButtonSize;
	shape?: IconButtonShape;
	height?: "auto" | "fill";
	width?: "auto" | "fill";
	isDisabled?: boolean;
	hoverStyle?: IconButtonHoverStyle;
	attachedOn?: ControlAttachmentOptions;
	tooltip?: string;
	isActive?: boolean;
	bubbleClick?: boolean;
	isPlaceholder?: boolean;
	tabIndex?: number;
} & (
	{
		kind: Exclude<IconButtonKind, "transparent-colored" | "primary">;
		icon: FontIconName | SvgIconValue;
	}
	|
	{
		kind: "transparent-colored";
		icon: IconValue | ExplicitInheritedTransparentColoredIconValue;
	}
	|
	{
		kind: "primary";
		icon: IconValue;
		color?: PrimaryButtonColor;
	}
);

export const IconButton = forwardRef(
	(props: IconButtonProps, ref: Ref<HTMLButtonElement>): ReactElement => {
		const {
			onClick,
			trackingId,
			hasNoBorderRadius = false,
			kind,
			size = "normal",
			shape = "square",
			height = "auto",
			width = "auto",
			icon: iconFromProps,
			isDisabled = false,
			hoverStyle = "default",
			attachedOn = EMPTY_ARRAY,
			tooltip,
			isActive = false,
			bubbleClick,
			isPlaceholder = false,
			tabIndex,
			onKeyDown
		} = props;

		const icon = useMemo(
			(): IconValue => {
				let result: IconValue | ExplicitInheritedTransparentColoredIconValue = iconFromProps;

				if (isExplicitInheritedTransparentColoredIconValue(result)) {
					result = result.name;
				}

				if (isDisabled) {
					result = unsetIconColor(result);
				}

				return result;
			},
			[iconFromProps, isDisabled]
		);

		const clickHandler = useCallback(
			(args: MouseEvent) => {
				const {
					clientX,
					clientY
				} = args;

				if (bubbleClick === false) {
					args.preventDefault();
					args.stopPropagation();
				}

				if (onClick) {
					onClick({ clientX, clientY });
				}
			},
			[bubbleClick, onClick]
		);

		const doubleClickHandler = useCallback(
			(args: MouseEvent) => {
				if (bubbleClick === false) {
					args.preventDefault();
					args.stopPropagation();
				}
			},
			[bubbleClick]
		);

		const {
			attachedOn: attachmentsFromContext
		} = useContext(ControlAttachmentContext);

		const attachedOnClassNames: string = getAttachmentClassNames(attachedOn, attachmentsFromContext);

		return (
			<ButtonBase
				className={classNames(
					styles.root,
					{
						[styles.root_square]: shape === "square",
						[styles.root_noBorderRadius]: hasNoBorderRadius,
						[styles.root_fillHeight]: height === "fill",
						[styles.root_fillWidth]: width === "fill",
						[styles.root_large]: size === "large",
						[styles.root_placeholder]: isPlaceholder,
						[styles.root_active]: isActive,
						[styles.root_inheritColor]: isExplicitInheritedTransparentColoredIconValue(iconFromProps)
					},
					kindToHoverStyleMapping[kind][hoverStyle],
					attachedOnClassNames
				)}
				onClick={clickHandler}
				onDoubleClick={doubleClickHandler}
				trackingId={trackingId}
				disabled={isDisabled}
				ref={ref}
				title={tooltip}
				tabIndex={tabIndex}
				aria-label={tooltip}
				onKeyDown={onKeyDown}
			>
				<IconButtonRenderer
					kind={kind}
					color={props.kind === "primary" ? props.color : undefined}
					size={size}
					icon={icon}
					attachedOnClassNames={attachedOnClassNames}
				/>
			</ButtonBase>
		);
	}
);
IconButton.displayName = "IconButton";

export function isExplicitInheritedTransparentColoredIconValue(
	icon: IconValue | ExplicitInheritedTransparentColoredIconValue
): icon is ExplicitInheritedTransparentColoredIconValue {
	return typeof icon === "object"
		&& icon.kind === "font"
		&& icon.color === "inherit";
}

export type IconButtonShape =
	| "square"
	| "round";

export type IconButtonSize =
	| "normal"
	| "large";

export type IconButtonKind =
	| "primary"
	| "secondary"
	| "dashed"
	| "transparent"
	| "transparent-colored"
	| "gradient";

export type IconButtonHoverStyle =
	| "default"
	| "inversed";